import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Card, CardBody, Row, Col, Button, Badge } from "reactstrap";
import { Link, useHistory } from "react-router-dom";
import CompanyFormModal from "./Companyformmodal";
import axiosInstance from "../../../Axiosinstance";
import { toast } from "react-toastify";
import DataTable from "./Datatable";


const PAGE_SIZE = 10;

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const RegistrationBadge = ({ company }) => {
  if (!company.is_company_verified) {
    return <span className="text-danger">Not Registered</span>;
  }
  if (company.verified_by_tag8admin) {
    return <span>Registered by Admin</span>;
  }
  return <span className="text-warning">Registered by Company</span>;
};

const CompaniesTable = () => {
    const history = useHistory();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const openCreateModal = () => {
    setIsEditing(false);
    setEditingCompany(null);
    setIsModalOpen(true);
  };

  const toggleModal = () =>
    setIsModalOpen((prev) => {
      const next = !prev;
      if (!next) {
        setIsEditing(false);
        setEditingCompany(null);
      }
      return next;
    });

  const fetchCompanies = useCallback(async (page = 1, search = "") => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page,
        limit: PAGE_SIZE,
        ...(search?.trim() && { search: search.trim() }),
      });
      const res = await axiosInstance.get(`/api/getcompanydetails?${params}`);
      if (res?.data?.status) {
        setCompanies(
          (res.data.companies || []).filter((company) => company.company_name)
        );
        setTotalPages(res?.data?.pagination?.totalPages ?? 1);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Unable to fetch companies");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies(currentPage, searchTerm);
  }, [fetchCompanies, currentPage, searchTerm]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleEditCompany = (company) => {
    setIsEditing(true);
    setEditingCompany(company);
    setIsModalOpen(true);
  };

  const handleViewCompany = (company) => {
    history.push(`/companymanagement/${company.company_uid}`);
  };

  const handleViewWallet = (company) => {
    history.push(`/companymanagement/${company.company_uid}/wallet`);
  };

  const columns = useMemo(
    () => [
      {
        dataField: "sl_no",
        text: "Sl. No.",
        formatter: (cell, row, rowIndex) =>
          (currentPage - 1) * PAGE_SIZE + rowIndex + 1,
      },
      {
        dataField: "company_name",
        text: "Company Name",
        formatter: (cell) =>
          cell ? (
            <Link to="#" className="text-dark">
              {cell}
            </Link>
          ) : (
            <span className="text-muted">-</span>
          ),
      },
      {
        dataField: "no_of_branches",
        text: "Branches",
        formatter: (cell) => <Badge color="info">{cell || 0}</Badge>,
      },
      {
        dataField: "wallet_balance",
        text: "Wallet",
        formatter: (cell) =>
          cell === null || cell === undefined
            ? "—"
            : `₹${Number(cell).toLocaleString("en-IN")}`,
      },
      {
        dataField: "created_date",
        text: "Created On",
        formatter: (cell) => formatDate(cell),
      },
      {
        dataField: "updated_date",
        text: "Updated On",
        formatter: (cell) => formatDate(cell),
      },
      {
        dataField: "registration",
        text: "Registration",
        formatter: (cell, row) => <RegistrationBadge company={row} />,
      },
      {
        dataField: "view",
        text: "View",
        formatter: (cell, row) => (
          <Button
            color="light"
            size="sm"
            onClick={() => handleViewCompany(row)}
            title="View company"
          >
            <i className="mdi mdi-eye-outline font-size-18 text-primary" />
          </Button>
        ),
      },
      {
        dataField: "wallet",
        text: "Wallet",
        formatter: (cell, row) => (
          <Button
            color="light"
            size="sm"
            onClick={() => handleViewWallet(row)}
            title="Wallet & history"
          >
            <i className="mdi mdi-wallet-outline font-size-18 text-primary" />
          </Button>
        ),
      },
      {
        dataField: "action",
        text: "Action",
        formatter: (cell, row) => (
          <Button
            color="light"
            size="sm"
            onClick={() => handleEditCompany(row)}
            title="Edit company"
          >
            <i className="mdi mdi-pencil font-size-18 text-primary" />
          </Button>
        ),
      },
    ],
    [currentPage]
  );

  return (
    <Card>
      <CardBody>
        <Row className="mb-3 justify-content-between align-items-center">
          <Col md="6" sm="12" className="mb-2 mb-md-0">
            <h4 className="card-title mb-0">Companies</h4>
          </Col>
          <Col md="6" sm="12">
            <div className="d-flex justify-content-md-end justify-content-start flex-wrap gap-2">
              <Button
                color="primary"
                className="font-16 btn btn-primary px-4 shadow-sm"
                onClick={openCreateModal}
                style={{ borderRadius: "999px", minWidth: "160px" }}
              >
                Create Company
              </Button>
            </div>
          </Col>
        </Row>

        <DataTable
          keyField="company_uid"
          columns={columns}
          data={companies}
          page={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          loading={loading}
          searchPlaceholder="Search companies"
        />

        <CompanyFormModal
          isOpen={isModalOpen}
          toggle={toggleModal}
          isEditing={isEditing}
          editingCompany={editingCompany}
          onSaved={() => fetchCompanies(currentPage, searchTerm)}
        />
      </CardBody>
    </Card>
  );
};

export default CompaniesTable;